import React, { useState, useEffect } from 'react';

const StockForm = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    itemName: '',
    partNumber: '',
    quantity: 0,
    location: '',
    unitCost: 0,
    type: 'part',
    reorderLevel: 5,
    description: '',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        itemName: initialData.itemName || '',
        partNumber: initialData.partNumber || '',
        quantity: initialData.quantity || 0,
        location: initialData.location || '',
        unitCost: initialData.unitCost || 0,
        type: initialData.type || 'part',
        reorderLevel: initialData.reorderLevel || 5,
        description: initialData.description || '',
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.itemName.trim()) {
      newErrors.itemName = 'Item name is required';
    }

    if (!formData.partNumber.trim()) {
      newErrors.partNumber = 'Part number is required';
    }

    if (formData.quantity < 0) {
      newErrors.quantity = 'Quantity cannot be negative';
    }

    if (formData.unitCost < 0) {
      newErrors.unitCost = 'Unit cost cannot be negative';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Convert numeric fields
    const submitData = {
      ...formData,
      quantity: parseFloat(formData.quantity),
      unitCost: parseFloat(formData.unitCost),
      reorderLevel: parseInt(formData.reorderLevel),
      totalValue: parseFloat(formData.quantity) * parseFloat(formData.unitCost),
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Item Name */}
        <div className="sm:col-span-2">
          <label htmlFor="itemName" className="block text-sm font-semibold text-gray-700">
            Item Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="itemName"
            id="itemName"
            value={formData.itemName}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-xl shadow-sm text-sm py-2 px-3 ${
              errors.itemName
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="e.g., OIL FILTER (TOYOTA)"
          />
          {errors.itemName && <p className="mt-1 text-xs text-red-600">{errors.itemName}</p>}
        </div>

        {/* Part Number */}
        <div>
          <label htmlFor="partNumber" className="block text-sm font-semibold text-gray-700">
            Part Number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="partNumber"
            id="partNumber"
            value={formData.partNumber}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-xl shadow-sm text-sm py-2 px-3 ${
              errors.partNumber
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="e.g., 90915-YZZE1"
          />
          {errors.partNumber && <p className="mt-1 text-xs text-red-600">{errors.partNumber}</p>}
        </div>

        {/* Type */}
        <div>
          <label htmlFor="type" className="block text-sm font-semibold text-gray-700">
            Type
          </label>
          <select
            name="type"
            id="type"
            value={formData.type}
            onChange={handleChange}
            className="mt-1 block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm py-2 px-3"
          >
            <option value="part">Part</option>
            <option value="consumable">Consumable</option>
          </select>
        </div>

        {/* Quantity */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-semibold text-gray-700">
            Quantity <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="quantity"
            id="quantity"
            value={formData.quantity}
            onChange={handleChange}
            min="0"
            step="0.1"
            className={`mt-1 block w-full rounded-xl shadow-sm text-sm py-2 px-3 ${
              errors.quantity
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            }`}
          />
          {errors.quantity && <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>}
        </div>

        {/* Unit Cost */}
        <div>
          <label htmlFor="unitCost" className="block text-sm font-semibold text-gray-700">
            Unit Cost (₦) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="unitCost"
            id="unitCost"
            value={formData.unitCost}
            onChange={handleChange}
            min="0"
            step="0.01"
            className={`mt-1 block w-full rounded-xl shadow-sm text-sm py-2 px-3 ${
              errors.unitCost
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            }`}
          />
          {errors.unitCost && <p className="mt-1 text-xs text-red-600">{errors.unitCost}</p>}
        </div>

        {/* Location */}
        <div>
          <label htmlFor="location" className="block text-sm font-semibold text-gray-700">
            Location <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="location"
            id="location"
            value={formData.location}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-xl shadow-sm text-sm py-2 px-3 ${
              errors.location
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="e.g., AY-A-1A1 or OPEN FLOOR"
          />
          {errors.location && <p className="mt-1 text-xs text-red-600">{errors.location}</p>}
        </div>

        {/* Reorder Level */}
        <div>
          <label htmlFor="reorderLevel" className="block text-sm font-semibold text-gray-700">
            Reorder Level
          </label>
          <input
            type="number"
            name="reorderLevel"
            id="reorderLevel"
            value={formData.reorderLevel}
            onChange={handleChange}
            min="0"
            className="mt-1 block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm py-2 px-3"
          />
          <p className="mt-1 text-xs text-gray-500">Alert when stock falls below this level</p>
        </div>

        {/* Description */}
        <div className="sm:col-span-2">
          <label htmlFor="description" className="block text-sm font-semibold text-gray-700">
            Description / Notes
          </label>
          <textarea
            name="description"
            id="description"
            rows={3}
            value={formData.description}
            onChange={handleChange}
            className="mt-1 block w-full rounded-xl border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm py-2 px-3"
            placeholder="Additional information about this item..."
          />
        </div>

        {/* Total Value (Calculated) */}
        <div className="sm:col-span-2 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-200">
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold text-gray-700">Total Value:</span>
            <span className="text-lg font-black text-blue-600">
              ₦{(parseFloat(formData.quantity || 0) * parseFloat(formData.unitCost || 0)).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-xl shadow-md text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-all duration-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
        >
          {initialData ? 'Update Stock' : 'Add Stock'}
        </button>
      </div>
    </form>
  );
};

export default StockForm;
